import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X, Check } from "lucide-react";
import { cn } from "../../lib/utils";

export interface AutocompleteOption {
  value: string;
  label: string;
}

export interface AutocompleteProps {
  options: AutocompleteOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
  /** Map of option values to colors (for colored dot indicator) */
  colorMap?: Record<string, string>;
  /** Whether the value can be cleared (shows X button). Default: true */
  clearable?: boolean;
  /** Max height for dropdown list. Default: 70vh */
  maxDropdownHeight?: string;
}

export function Autocomplete({
  options,
  value,
  onChange,
  placeholder = "Seleziona...",
  disabled = false,
  className,
  icon,
  colorMap,
  clearable = true,
  maxDropdownHeight = "70vh",
}: AutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (isOpen && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        } else {
          setIsOpen(true);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSearch("");
        break;
      case "Tab":
        setIsOpen(false);
        setSearch("");
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setHighlightedIndex(0); // Reset highlighted index when search changes
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div
        className={cn(
          "flex items-center h-10 w-full rounded-lg border bg-card text-sm transition-all duration-200",
          "focus-within:border-[#284899]",
          value && clearable ? "border-[#284899]" : "border-border",
          disabled && "opacity-50 cursor-not-allowed",
          icon ? "pl-7" : "pl-3"
        )}
      >
        {icon && (
          <div className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            {icon}
          </div>
        )}
        {/* Color dot indicator when value is selected */}
        {colorMap && value && !isOpen && (
          <div
            style={{
              backgroundColor: colorMap[value] || '#64748b',
              width: 10,
              height: 10,
              minWidth: 10,
              minHeight: 10,
              borderRadius: '50%',
              flexShrink: 0,
              marginLeft: 4,
              border: '1px solid rgba(255,255,255,0.3)'
            }}
          />
        )}
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? search : selectedOption?.label || ""}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none",
            "min-w-0 pr-1",
            colorMap && value && colorMap[value] && !isOpen && "ml-1.5"
          )}
        />
        <div className="flex items-center gap-0.5 pr-1.5">
          {value && !disabled && clearable && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              if (disabled) return;
              if (isOpen) {
                setIsOpen(false);
                setSearch("");
              } else {
                setIsOpen(true);
                inputRef.current?.focus();
              }
            }}
            className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={listRef}
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "absolute z-50 mt-1 w-full overflow-auto rounded-lg",
              "bg-card border border-border",
              "no-scrollbar"
            )}
            style={{ maxHeight: maxDropdownHeight }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                Nessun risultato
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  title={option.label}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm flex items-center justify-between",
                    "transition-colors duration-100 group/option",
                    index === highlightedIndex && "overlay-solid-secondary",
                    option.value === value && "text-[#284899] font-medium"
                  )}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {colorMap && (
                      <span
                        style={{
                          backgroundColor: colorMap[option.value] || '#64748b',
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          display: 'block',
                          flexShrink: 0,
                          boxShadow: '0 0 0 1px rgba(255,255,255,0.2)'
                        }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="truncate group-hover/option:whitespace-normal group-hover/option:break-words">
                      {option.label}
                    </span>
                  </span>
                  {option.value === value && (
                    <Check className="h-3 w-3 text-[#284899] flex-shrink-0 ml-2" />
                  )}
                </button>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

